// src/theme.js
// DynaCloud design system — "Meridian"
// Dark, atlas-inspired enterprise aesthetic: ink-navy canvas, azure→emerald
// accents, glass surfaces. Single source of truth for colour, type and shape.
import { createTheme, alpha } from '@mui/material/styles';

export const ink = {
  base: '#05080F', // page canvas
  raised: '#0A101F', // panels, drawers
  overlay: '#0E1628', // popovers, sticky headers
};

export const azure = {
  50: '#EFF9FF',
  100: '#DEF1FD',
  200: '#B6E5FC',
  300: '#7DD3FC',
  400: '#38BDF8',
  500: '#0EA5E9',
  600: '#0284C7',
  700: '#0369A1',
  800: '#075985',
  900: '#0C4A6E',
};

export const emerald = {
  200: '#A7F3D0',
  300: '#6EE7B7',
  400: '#34D399',
  500: '#10B981',
  600: '#059669',
};

export const slate = {
  100: '#E6EDF7',
  200: '#CBD7E8',
  300: '#AEBDD4',
  400: '#9AA7BD',
  500: '#7B899F',
  600: '#5D6B81',
  700: '#3D4A5E',
  800: '#1E2939',
  900: '#111A2B',
};

const LATIN_STACK = [
  '"Manrope"',
  '"Segoe UI"',
  'system-ui',
  '-apple-system',
  'Roboto',
  'Arial',
  'sans-serif',
].join(',');

const ARABIC_STACK = [
  '"IBM Plex Sans Arabic"',
  '"Segoe UI"',
  'Tahoma',
  '"Amiri"',
  'system-ui',
  'sans-serif',
].join(',');

export const line = alpha(slate[400], 0.16);
export const lineStrong = alpha(slate[400], 0.28);
export const glass = alpha('#FFFFFF', 0.03);
export const glassHover = alpha('#FFFFFF', 0.055);

export const gradientText = {
  backgroundImage: `linear-gradient(92deg, ${slate[100]} 0%, ${azure[300]} 55%, ${emerald[300]} 110%)`,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
};

export default function getTheme(direction = 'ltr') {
  const isRTL = direction === 'rtl';
  const fontFamily = isRTL ? ARABIC_STACK : LATIN_STACK;
  // Arabic script needs more vertical breathing room and no negative tracking.
  const tighten = (v) => (isRTL ? '0em' : v);

  return createTheme({
    direction,
    palette: {
      mode: 'dark',
      primary: {
        light: azure[300],
        main: azure[400],
        dark: azure[600],
        contrastText: '#04121F',
      },
      secondary: {
        light: emerald[300],
        main: emerald[400],
        dark: emerald[600],
        contrastText: '#04120D',
      },
      background: {
        default: ink.base,
        paper: ink.raised,
      },
      divider: line,
      text: {
        primary: slate[100],
        secondary: slate[400],
        disabled: slate[600],
      },
      success: { main: emerald[400] },
      info: { main: azure[300] },
    },
    shape: { borderRadius: 14 },
    spacing: 8,
    typography: {
      fontFamily,
      h1: {
        fontFamily,
        fontWeight: 800,
        fontSize: 'clamp(2.3rem, 4.3vw, 3.55rem)',
        lineHeight: isRTL ? 1.35 : 1.08,
        letterSpacing: tighten('-0.025em'),
      },
      h2: {
        fontFamily,
        fontWeight: 800,
        fontSize: 'clamp(1.9rem, 3.2vw, 2.7rem)',
        lineHeight: isRTL ? 1.4 : 1.14,
        letterSpacing: tighten('-0.02em'),
      },
      h3: {
        fontFamily,
        fontWeight: 700,
        fontSize: 'clamp(1.35rem, 2vw, 1.6rem)',
        lineHeight: isRTL ? 1.5 : 1.25,
        letterSpacing: tighten('-0.01em'),
      },
      h4: {
        fontFamily,
        fontWeight: 700,
        fontSize: '1.25rem',
        lineHeight: isRTL ? 1.55 : 1.3,
      },
      h5: { fontFamily, fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.4 },
      h6: { fontFamily, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.4 },
      subtitle1: {
        fontFamily,
        fontSize: '1.125rem',
        lineHeight: isRTL ? 2 : 1.7,
        color: slate[300],
      },
      body1: { fontFamily, fontSize: '1.02rem', lineHeight: isRTL ? 2 : 1.75 },
      body2: { fontFamily, fontSize: '0.92rem', lineHeight: isRTL ? 1.9 : 1.65 },
      overline: {
        fontFamily,
        fontWeight: 700,
        fontSize: '0.72rem',
        letterSpacing: isRTL ? '0.06em' : '0.22em',
        textTransform: 'uppercase',
      },
      button: {
        fontFamily,
        fontWeight: 700,
        textTransform: 'none',
        letterSpacing: tighten('0.01em'),
      },
      caption: { fontFamily, lineHeight: 1.6 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: ink.base,
            color: slate[100],
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingInline: 22,
            paddingBlock: 10,
            fontSize: '0.95rem',
            transition: 'all .25s cubic-bezier(.2,.8,.2,1)',
          },
          sizeLarge: {
            paddingInline: 28,
            paddingBlock: 13,
            fontSize: '1rem',
          },
          sizeSmall: { paddingInline: 16, paddingBlock: 6, fontSize: '0.85rem' },
          containedPrimary: {
            color: '#031320',
            backgroundImage: `linear-gradient(120deg, ${azure[300]} 0%, ${azure[400]} 45%, ${emerald[400]} 130%)`,
            boxShadow: `0 8px 28px -8px ${alpha(azure[400], 0.55)}`,
            '&:hover': {
              backgroundImage: `linear-gradient(120deg, ${azure[200]} 0%, ${azure[300]} 45%, ${emerald[300]} 130%)`,
              boxShadow: `0 12px 34px -8px ${alpha(azure[400], 0.7)}`,
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            borderColor: lineStrong,
            color: slate[100],
            '&:hover': {
              borderColor: alpha(azure[300], 0.6),
              backgroundColor: alpha(azure[400], 0.08),
            },
          },
          text: {
            color: slate[300],
            '&:hover': { color: slate[100], backgroundColor: glass },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: slate[300],
            '&:hover': { color: slate[100], backgroundColor: glass },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: glass,
            border: `1px solid ${line}`,
            borderRadius: 20,
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: ink.raised,
            backgroundImage: 'none',
            borderInlineStart: `1px solid ${line}`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 600,
            backgroundColor: glass,
            border: `1px solid ${line}`,
            color: slate[200],
          },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: {
          root: { color: azure[300], textDecorationColor: alpha(azure[300], 0.4) },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: line } },
      },
      MuiListItemIcon: {
        styleOverrides: { root: { color: azure[300], minWidth: 38 } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: ink.overlay,
            border: `1px solid ${line}`,
            fontSize: '0.8rem',
          },
        },
      },
    },
  });
}
